import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Alert from 'react-s-alert';
import i18next from 'i18next';
import LinkifyIt from 'linkify-it';
import Icon from 'components/Icon';

import styled from 'styled-components';

const linkify = LinkifyIt();

export const isLink = input => linkify.pretest(input) && linkify.test(input);

export const cleanInput = rawInput => rawInput.trim().replace(' ', '%20');

const ChatInput = styled.input(({ theme }) => ({
  width: '100%',
  borderRadius: 2,
  fontSize: theme.font.size.small,
  background: theme.color.wfGrey100,
  padding: theme.space[2],
  '&::placeholder': {
    color: theme.color.wfBlack,
  },
}));

const Input = styled.input`
  border: none;
  width: calc(100% - 20px);
  float: left;
  background-color: ${props => props.theme.color.wfGrey};
  height: 44px;
  color: ${props => props.theme.color.wfWhite};
  margin-left: 3px;
  font-size: 15px;
  &::placeholder {
    color: rgba(255, 255, 255, 0.8);
  }
  &:active {
    outline: 0;
    border: none;
  }
`;

@observer
export class LinkInput extends React.Component {
  setLink = e => {
    const { store } = this.props;
    const {
      reference,
      onChange,
      toolbarStore: { inputValue, SetInputStatus, SetInput },
    } = store;
    e.preventDefault();

    const input = cleanInput(inputValue);

    if (isLink(input)) {
      const href = linkify.match(input).pop().url;
      const linkChange = reference.wrapInline({
        type: 'link',
        data: { href },
      });
      SetInputStatus(false);
      onChange(linkChange.moveToEnd().focus());
      SetInput();
    } else {
      Alert.error(i18next.t("Hmm.. That link doesn't look right!"));
    }
  };

  handleKeyDown = e => {
    if (e.which === 13) {
      this.setLink(e);
    }
  };

  render() {
    const { store } = this.props;
    const {
      toolbarStore: { SetInputStatus, SetInput, inputValue },
    } = store;
    const isEmpty = inputValue.length === 0;
    const isValid = isLink(cleanInput(inputValue));

    const InputStyles = store.mediaMenuAlwaysVisible ? ChatInput : Input;

    return (
      <InputWrapper>
        <InputStyles
          autoFocus
          type='text'
          className='inputNoBorder'
          placeholder='Paste or type a link...'
          value={inputValue}
          onChange={e => SetInput(e.target.value)}
          onKeyDown={this.handleKeyDown}
        />
        {store.mediaMenuAlwaysVisible ? (
          <Icon
            name='error'
            onClick={() => SetInputStatus(false)}
            style={{ position: 'absolute', right: 8, top: 8 }}
          />
        ) : isEmpty || !isValid ? (
          <IconStyled
            color='red'
            name='error'
            onClick={() => SetInputStatus(false)}
          />
        ) : (
          <IconStyled name='success' onClick={this.setLink} />
        )}
      </InputWrapper>
    );
  }
}

const InputWrapper = styled.div`
  position: relative;
  min-width: 376px;
  height: 43px;
  border: none;
  float: left;
`;

const IconStyled = styled(Icon)`
  color: ${props => props.theme.color.wfWhite};
  position: absolute;
  right: 18px;
  top: 50%;
  cursor: pointer;
  transform: scale(1) translateY(-50%);
  transition: transform 50ms linear;
  &:hover {
    transform: scale(1.125) translateY(-50%);
  }
`;

LinkInput.propTypes = {
  store: PropTypes.object,
};
